import {
  LoginFormSchema,
  LoginFormType,
  RegisterFormSchema,
  RegisterFormType,
} from "@/schemas/auth";
import { clearLocalDB } from "@/indexedDB/db";
import { useNavigationState } from "@/zustand/NavigationState";
import { unsubscribeOnLogout } from "@/components/web-push/webPushUtils";

import {
  getKdfParams,
  login,
  logout,
  register,
  upgradeKdf,
} from "@/server/services/authService";
import {
  authSecretFromMk,
  decryptPrivateKey,
  deriveKey,
  deriveKeyWithSalt,
  encryptPrivateKey,
  generateKdfSalt,
  isValidKdfSalt,
  kekFromMk,
  generateKeyPair,
  hashPassword,
} from "@lib/crypto/crypto";

import {
  deleteAccount,
  getUserData,
  patchUserData,
} from "@/server/services/userService";
import { AuthState, useAuthState } from "@/zustand/AuthState";
import { useChatListState } from "@/zustand/ChatListState";
import { useConnectionState } from "@/zustand/ConnectionState";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import { useCallState } from "@/zustand/CallState";
import { useTypingState } from "@/zustand/TypingState";
import { useBfnCalculatorState } from "@/zustand/BfnCalculatorState";
import { queryClient } from "@/queryClient";
import { deleteImage } from "@/server/services/imageService";
import { clearCryptoCache } from "./cryptoCache";
import { clearCachedMedia } from "./mediaMemoryCache";
import { clearSealedDk } from "@/lib/crypto/sealedKey";
import { clearHotCache, clearWarmCache } from "./epochKeyCache";
import { clearDecryptionRetryState } from "./decryptionRetryOrchestrator";
import { clearDecryptionLogState } from "./decryptionError";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { DEFAULT_SUBSCRIPTION } from "@/server/types/subscription";
import {
  PendingAuthData,
  savePendingAuth,
  clearPendingAuth,
} from "@/lib/pendingAuth";
import { useTcGateState } from "@/zustand/TcGateState";
import { featureFlags } from "@/config/featureFlags";
import { createLogger } from "@/utils/debug";
import { safeSession } from "@/utils/safeStorage";
import { suppressEmeraldNote } from "@/lib/emeraldNote";

const log = createLogger("Auth");

// sessionStorage key used to signal ChatPage to run a follow-up syncChats()
// after registration. sessionStorage survives React StrictMode double-mounts
// (module-level vars are cleared on the first mount/unmount cycle) and is
// automatically wiped on tab close, making the lifecycle explicit.
const POST_REGISTRATION_SYNC_KEY = "orion:pendingPostRegistrationSync";

export const markNeedsPostRegistrationSync = (): void => {
  safeSession.set(POST_REGISTRATION_SYNC_KEY, "1");
};

/** Reads and atomically clears the flag. Returns true once, then false. */
export const consumeNeedsPostRegistrationSync = (): boolean => {
  if (safeSession.get(POST_REGISTRATION_SYNC_KEY) === "1") {
    safeSession.remove(POST_REGISTRATION_SYNC_KEY);
    return true;
  }
  return false;
};

const setUsername = useAuthState.getState().setUsername;
const setDerivedKey = useAuthState.getState().setDerivedKey;
const setToken = useAuthState.getState().setToken;
const setRefreshToken = useAuthState.getState().setRefreshToken;
const setPubKey = useAuthState.getState().setPubKey;
const setPrivKey = useAuthState.getState().setPrivKey;
const setEncryptedPassword = useAuthState.getState().setEncryptedPassword;
const setKdfVersion = useAuthState.getState().setKdfVersion;
const setKdfSalt = useAuthState.getState().setKdfSalt;

// Commits the full set of auth fields in one call.
// loginUser, registerUser, and commitPendingAuth all perform identical
// field-by-field mutations; centralising them here means a new auth field
// only needs to be added once.
type AuthBundle = {
  username: string;
  derivedKey: string;
  token: string;
  refreshToken: string;
  privateKeys: string;
  publicKeys: string;
  encryptedPassword?: string;
  subscription?: Parameters<typeof setSubscription>[0] | null;
  termsVersion?: string | null;
  // Account's KDF scheme (0 = v1, 2 = v2, 3 = v3). `derivedKey` is the private-key
  // KEK: for v1 the master key, for v2/v3 the HKDF-derived kek.
  kdfVersion: number;
  // Per-user random salt (hex) for v3; "" for v1/v2.
  kdfSalt: string;
};

const applyAuthBundle = ({
  username,
  derivedKey,
  token,
  refreshToken,
  privateKeys,
  publicKeys,
  encryptedPassword,
  subscription,
  termsVersion,
  kdfVersion,
  kdfSalt,
}: AuthBundle): void => {
  setUsername(username);
  setDerivedKey(derivedKey);
  setToken(token);
  setRefreshToken(refreshToken);
  setPrivKey(privateKeys);
  setPubKey(publicKeys);
  setEncryptedPassword(encryptedPassword);
  setKdfVersion(kdfVersion);
  setKdfSalt(kdfSalt);
  if (subscription) {
    setSubscription({ ...DEFAULT_SUBSCRIPTION, ...subscription });
  }
  if (termsVersion) {
    setAcceptedVersion(termsVersion);
  }
};

const seedCurrentUserAvatar = (
  username: string,
  profileImage?: string,
): void => {
  const { setUserAvatar, removeUserAvatar } = useUserAvatarMapState.getState();
  if (profileImage) {
    setUserAvatar(username, profileImage, new Date().toISOString(), true);
  } else {
    removeUserAvatar(username, true);
  }
};

const resetAuthState = useAuthState.getState().resetAuthState;
const resetNavigationState = useNavigationState.getState().resetNavigationState;
const resetChatListState = useChatListState.getState().resetChatListState;
const setSubscription = useSubscriptionState.getState().setSubscription;
const resetSubscription = useSubscriptionState.getState().resetSubscription;
const setTcRequired = useTcGateState.getState().setTcRequired;
const setAcceptedVersion = useTcGateState.getState().setAcceptedVersion;
const clearTcGate = useTcGateState.getState().clearTcGate;

export const loginUser = async (
  values: LoginFormType,
  turnstileToken?: string,
) => {
  try {
    // Validate the payload with the LoginFormSchema (zod)
    const isValidPayload = LoginFormSchema.safeParse(values);

    // If the payload is invalid, throw an error with the first error message
    if (!isValidPayload.success) {
      throw new Error(isValidPayload.error.errors[0].message);
    }

    // Ask the server which KDF scheme this account uses BEFORE deriving the
    // master key — v3 salts Argon2id with a per-user random salt we can only get
    // here. If the lookup fails, fall back to v1 (username salt, legacy).
    let kdfVersion = 0;
    let kdfSalt = "";
    try {
      const params = await getKdfParams(values.username);
      kdfVersion = params.kdf_version ?? 0;
      kdfSalt = params.kdf_salt ?? "";
    } catch (err) {
      // Fail closed. Guessing v1 for a v3 account derives the wrong master key and
      // 401s — indistinguishable from a wrong password — so any /kdf-params blip
      // would surface as a fleet-wide "wrong password" outage. Surface a
      // retryable error instead of deriving under an assumed scheme.
      log.warn("kdf-params lookup failed", err);
      throw new Error(
        "Couldn't verify your account settings. Please try again.",
      );
    }
    // A genuine v3 account ALWAYS has a valid random salt. The server returns
    // `{ kdf_version: 3, kdf_salt: "" }` as the anti-enumeration default for an
    // UNKNOWN username (and it's the shape a corrupt record would take). Treat
    // that as NOT-v3 and derive under v1, so the credential exchange returns a
    // normal "invalid credentials" 401 instead of deriveKeyWithSalt throwing
    // "Invalid KDF salt: must be at least 8 bytes" — which would otherwise make
    // every mistyped-username login fail with a confusing crypto error.
    if (kdfVersion >= 3 && !isValidKdfSalt(kdfSalt)) {
      kdfVersion = 0;
      kdfSalt = "";
    }
    const isV2Plus = kdfVersion >= 2; // HKDF credential/KEK split (v2 and v3)
    const isV3 = kdfVersion >= 3; // random-salt derivation

    // Master key (Argon2id). v3 uses the stored random salt; v1/v2 use the
    // username. Everything else derives from mk; it never leaves the device.
    const mk = isV3
      ? deriveKeyWithSalt(values.password, kdfSalt)
      : deriveKey(values.password, values.username);

    // v2+: credential = authSecret (unlinkable from the KEK); the private-key KEK
    // stored as `dk` is the HKDF kek. v1: credential = the legacy hash, dk = mk.
    const credential = isV2Plus
      ? authSecretFromMk(mk)
      : hashPassword(values.password, values.username);
    let derivedKey = isV2Plus ? kekFromMk(mk) : mk;

    // Make a request to the login endpoint
    const loginResponse = await login({
      username: values.username,
      password: credential,
      turnstileToken,
    });

    // T&C acceptance required.
    // Set token + refreshToken so requestApi can authorize the acceptTerms call.
    // Do NOT set username — keeps isLoggedIn=false so Protected routes never
    // mount and no background requests fire.
    if (
      loginResponse.requires_tc_acceptance &&
      loginResponse.pending_tc_version
    ) {
      setToken(loginResponse.token);
      setRefreshToken(loginResponse.refresh_token);
      savePendingAuth({
        username: loginResponse.username,
        derivedKey,
        token: loginResponse.token,
        refreshToken: loginResponse.refresh_token,
        privateKeys: loginResponse.private_keys,
        publicKeys: loginResponse.public_keys,
        encryptedPassword: loginResponse.encrypted_password,
        subscription: loginResponse.subscription,
        pendingTcVersion: loginResponse.pending_tc_version,
        kdfVersion: loginResponse.kdf_version ?? kdfVersion,
        kdfSalt: loginResponse.kdf_salt ?? kdfSalt,
      });
      setTcRequired(loginResponse.pending_tc_version);
      return { pendingTc: true };
    }

    // Opportunistic upgrade to v3 (from v1 or v2): unwrap the private key with
    // the CURRENT kek (`derivedKey`), then re-derive the master key under a fresh
    // random salt, re-wrap under the new kek, and swap the credential to the new
    // authSecret. No re-prompt (we hold the plaintext password this login).
    // Best-effort: on failure we stay on the current scheme for this session and
    // retry next login. Gated by the kdfV2 kill switch so ops can halt v2/v3
    // advancement mid-rollout; an already-upgraded account logs in above
    // regardless of this flag. Other devices re-derive with the new salt on
    // their next login (they 401 once, then fetch the v3 salt) — the accepted
    // forced-re-login cost of v3.
    let privateKeys = loginResponse.private_keys;
    let effectiveKdfVersion = loginResponse.kdf_version ?? kdfVersion;
    let effectiveKdfSalt = loginResponse.kdf_salt ?? kdfSalt;
    if (!isV3 && featureFlags.kdfV2) {
      try {
        setToken(loginResponse.token);
        setRefreshToken(loginResponse.refresh_token);
        // Unwrap with the current-scheme KEK (mk for v1, kekFromMk(mk) for v2) —
        // that is exactly `derivedKey` as computed above.
        const plaintextPrivateKey = await decryptPrivateKey(
          loginResponse.private_keys,
          derivedKey,
        );
        const newSalt = generateKdfSalt();
        const mk3 = deriveKeyWithSalt(values.password, newSalt);
        const newKek = kekFromMk(mk3);
        const rewrapped = await encryptPrivateKey(plaintextPrivateKey, newKek);
        const upgradeResult = await upgradeKdf(
          {
            auth_secret: authSecretFromMk(mk3),
            private_keys: rewrapped,
            kdf_version: 3,
            kdf_salt: newSalt,
          },
          // Pass the fresh token explicitly: `username` isn't committed until
          // applyAuthBundle below, so the request interceptor won't attach it.
          loginResponse.token,
        );
        if (upgradeResult.performed) {
          // We won the upgrade: the server now holds OUR salt, so adopt it.
          derivedKey = newKek;
          privateKeys = rewrapped;
          effectiveKdfVersion = 3;
          effectiveKdfSalt = newSalt;
        }
        // Else another device already upgraded (lost race, or the account was
        // already v3). Do NOT adopt our own minted salt — the server holds a
        // DIFFERENT salt and its v3-wrapped private key, which we don't have here.
        // Stay on the current-session scheme (self-consistent locally) and
        // converge on the next login, which fetches the server's salt + key.
        // change-password also re-fetches the authoritative scheme, so this
        // transient local/server mismatch can never be written back as a
        // permanent, mismatched credential.
      } catch (err) {
        log.warn("KDF v3 upgrade failed; staying on current scheme", err);
      }
    }

    // Normal login — commit auth state immediately.
    // Merge subscription with defaults: BE strips stripe_customer_id/stripe_subscription_id
    // from auth responses, so absent fields must default to null (not undefined).
    applyAuthBundle({
      username: loginResponse.username,
      derivedKey,
      token: loginResponse.token,
      refreshToken: loginResponse.refresh_token,
      privateKeys,
      publicKeys: loginResponse.public_keys,
      encryptedPassword: loginResponse.encrypted_password,
      subscription: loginResponse.subscription,
      termsVersion: loginResponse.terms_version,
      kdfVersion: effectiveKdfVersion,
      kdfSalt: effectiveKdfSalt,
    });

    // Profile/avatar seeding is best-effort: applyAuthBundle above has already
    // committed auth state, so a transient failure here must NOT propagate —
    // LoginForm calls logoutUser() on any rejection, which would tear down a
    // valid session over a non-fatal profile-fetch error (e.g. the Rust API
    // being briefly unreachable). Mirrors registerUser's guard.
    try {
      const userData = await getUserData(loginResponse.username);
      seedCurrentUserAvatar(loginResponse.username, userData.profile_image);
    } catch (avatarError) {
      log.warn("Failed to seed avatar after login", avatarError);
    }

    return { pendingTc: false };
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error(
      "Unable to log in. Please check your credentials or try again.",
    );
  }
};

export const registerUser = async (
  values: RegisterFormType,
  verifiedEmail: string,
  turnstileToken?: string,
) => {
  try {
    const isValidPayload = RegisterFormSchema.safeParse(values);
    if (!isValidPayload.success) {
      throw new Error(isValidPayload.error.errors[0].message);
    }

    const keyPair = generateKeyPair();
    // New accounts register on v3 (per-user random salt, kek-wrapped private
    // key, authSecret credential) unless the kdfV2 kill switch is off, in which
    // case they register on the legacy v1 scheme (mk both wraps the key and —
    // hashed — is the credential, username salt). The wrap key, credential, and
    // kdf_version/kdf_salt must all agree or the account is unusable.
    const useV3 = featureFlags.kdfV2;
    const kdfSalt = useV3 ? generateKdfSalt() : "";
    const mk = useV3
      ? deriveKeyWithSalt(values.password, kdfSalt)
      : deriveKey(values.password, values.username);
    const derivedKey = useV3 ? kekFromMk(mk) : mk;
    const encryptedPrivateKey = await encryptPrivateKey(
      keyPair.privateKeys,
      derivedKey,
    );
    const credential = useV3
      ? authSecretFromMk(mk)
      : hashPassword(values.password, values.username);

    // Strip FE-only field before sending to the API
    const { acceptedTc: _, ...apiValues } = values;

    const registerResponse = await register({
      ...apiValues,
      email: verifiedEmail,
      password: credential,
      confirmPassword: credential,
      private_keys: encryptedPrivateKey,
      public_keys: keyPair.publicKeys,
      accepted_tc: true,
      kdf_version: useV3 ? 3 : 0,
      kdf_salt: kdfSalt,
      turnstileToken,
    });

    // Brand-new accounts never experienced the theme change the Midnight
    // Emerald announcement describes — mark it seen so it never shows.
    suppressEmeraldNote();

    // If the server requires TC acceptance (e.g. version mismatch), open the TC
    // gate the same way loginUser does — do not commit auth state yet.
    if (
      registerResponse.requires_tc_acceptance &&
      registerResponse.pending_tc_version
    ) {
      setToken(registerResponse.token);
      setRefreshToken(registerResponse.refresh_token);
      savePendingAuth({
        username: registerResponse.username,
        derivedKey,
        token: registerResponse.token,
        refreshToken: registerResponse.refresh_token,
        privateKeys: registerResponse.private_keys,
        publicKeys: registerResponse.public_keys,
        encryptedPassword: registerResponse.encrypted_password,
        subscription: registerResponse.subscription,
        pendingTcVersion: registerResponse.pending_tc_version,
        kdfVersion: registerResponse.kdf_version ?? (useV3 ? 3 : 0),
        kdfSalt: registerResponse.kdf_salt ?? kdfSalt,
      });
      setTcRequired(registerResponse.pending_tc_version);
      // The Baptizer still runs regardless of the TC gate, so signal ChatPage
      // to schedule the follow-up sync once the user accepts T&C and lands there.
      markNeedsPostRegistrationSync();
      return { pendingTc: true };
    }

    // Commit auth state directly from register response — no separate login call needed
    applyAuthBundle({
      username: registerResponse.username,
      derivedKey,
      token: registerResponse.token,
      refreshToken: registerResponse.refresh_token,
      privateKeys: registerResponse.private_keys,
      publicKeys: registerResponse.public_keys,
      encryptedPassword: registerResponse.encrypted_password,
      subscription: registerResponse.subscription,
      termsVersion: registerResponse.terms_version,
      kdfVersion: registerResponse.kdf_version ?? (useV3 ? 3 : 0),
      kdfSalt: registerResponse.kdf_salt ?? kdfSalt,
    });

    // Profile/avatar seeding is best-effort: applyAuthBundle above has already
    // committed auth state, so a transient failure here must NOT propagate —
    // RegisterForm calls logoutUser() on any rejection, which would wipe a
    // genuinely-created session over a non-fatal profile-fetch error.
    try {
      const userData = await getUserData(registerResponse.username);
      seedCurrentUserAvatar(registerResponse.username, userData.profile_image);
    } catch (avatarError) {
      log.warn("Failed to seed avatar after registration", avatarError);
    }

    markNeedsPostRegistrationSync();
    return { pendingTc: false };
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error(
      "Unable to register. Please check your credentials or try again.",
    );
  }
};

export const logoutUser = (
  options: {
    /**
     * Account-deletion path: the account no longer exists, so the web-push
     * unsubscribe emit and POST /umbra/auth/logout would be guaranteed
     * 401/404 noise (and the breadcrumb interceptor records every terminal
     * failure). Local teardown is unchanged.
     */
    skipServerLogout?: boolean;
  } = {},
) => {
  const refreshToken = useAuthState.getState().refreshToken;
  const accessToken = useAuthState.getState().tk;

  // Use the socket from the connection state
  const currentSocket = useConnectionState.getState().socket;
  const username = useAuthState.getState().username;

  // REMOVE ALL 'PERSISTED' STATES FROM ZUSTAND immediately
  // so the UI responds instantly without waiting for async operations

  // reset auth state
  resetAuthState();
  // Drop the user-events identity snapshot — a deliberate logout ends the
  // identity, and a stale snapshot could wrongly veto the NEXT account's own
  // delete event on this tab. Dynamic import: userEventsHandler statically
  // imports this module, so a static import back would cycle.
  void import("@/socketIO/userEventsHandler")
    .then((m) => m.resetLastKnownUsername())
    .catch(() => {});
  // clear T&C gate flag and any pending auth bundle
  clearTcGate();
  clearPendingAuth();
  // reset subscription state
  resetSubscription();
  // reset app init state
  resetChatListState();
  // Reset the navigation state
  resetNavigationState();
  // Reset the user avatar map
  useUserAvatarMapState.getState().resetUserAvatarMap();
  // Reset call state (active calls, call UI, blinking indicators)
  useCallState.getState().resetCallState();
  // Reset ephemeral typing state so a next account logging in within the 10s
  // expiry window can't briefly see the previous session's typers on a shared
  // chat id.
  useTypingState.getState().resetTyping();
  // Fully unmount the BFN Calculator (not just close): with keep-alive a
  // close leaves the previous account's iframe + gateway session warm in the
  // tree — that must never survive into the next account.
  useBfnCalculatorState.getState().unmount();

  // Clear all react cached queries
  queryClient.clear();

  // Clear in-memory crypto caches (userPK, csk) for this tab
  clearCryptoCache();
  // Drop decrypted attachment blobs — logout is SPA-only (no reload), so this
  // plaintext would otherwise survive into the next account's session.
  clearCachedMedia();
  // Remove the sealed master key so nothing recoverable is left after logout.
  // Best-effort/fire-and-forget: resetAuthState above already dropped the
  // in-memory dk, and the record is overwritten on the next login. A pending
  // seal from a racing render can't win — the store's dk is now empty.
  void clearSealedDk();
  // Clear in-memory epoch hot cache (plaintext CSKs). The warm cache lives in
  // IndexedDB and is wiped by clearLocalDB on logout, so it is not cleared here.
  clearHotCache();
  clearDecryptionRetryState();
  // Lazy import to break a circular dependency: syncDecryptionPass imports
  // isUserAuthenticated from this module. Hydration cancellation is best-effort
  // (fire-and-forget) so it doesn't need to be synchronous.
  void import("@/indexedDB/services/syncDecryptionPass")
    .then((m) => m.resetHydrationState())
    .catch(() => {});
  // Clear per-chat pinned-reconcile chains (tiny bounded leak otherwise). Lazy
  // import to break the auth → pinnedReconcile → umbra_messages → auth cycle.
  void import("@/middleware/pinnedReconcile")
    .then((m) => m.clearReconcileChains())
    .catch(() => {});
  clearDecryptionLogState();

  // Clear IndexedDB tables immediately so useLiveQuery stops returning stale data.
  // Fire-and-forget — table.clear() is fast, no need to await.
  clearLocalDB().catch(() => {});

  // Run slow async cleanup in the background (web push, API call)
  // without blocking the caller
  if (!options.skipServerLogout) {
    (async () => {
      // Unsubscribe from web push notifications (has up to 2s timeout)
      if (currentSocket && username) {
        try {
          await unsubscribeOnLogout(currentSocket, username);
        } catch {
          // ignore — don't let web push errors prevent logout
        }
      }

      if (refreshToken) {
        try {
          await logout(refreshToken, accessToken);
        } catch {
          // ignore logout errors
        }
      }
    })().catch(() => {});
  }
};

export const isUserAuthenticated = (): AuthState | null => {
  const { username, dk, tk, ePuK, ePrK, kdfVersion, kdfSalt } =
    useAuthState.getState();

  // Check that every property is present and truthy (kdfVersion/kdfSalt are
  // excluded — 0/"" are valid for legacy v1/v2 accounts).
  if (username && dk && tk && ePuK && ePrK) {
    return { username, dk, tk, ePuK, ePrK, kdfVersion, kdfSalt };
  }

  // If any property is missing, return null (or handle as needed)
  return null;
};

export const updateUserAvatar = async (attachmentId: string): Promise<void> => {
  if (!attachmentId) {
    throw new Error("Attachment data not present");
  }

  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const userData = await getUserData(userFingerPrint.username);
    await patchUserData({
      ...userData,
      profile_image: attachmentId,
    });

    // update the user avatar map with the new attachment ID
    useUserAvatarMapState.getState().setUserAvatar(
      userFingerPrint.username,
      attachmentId,
      new Date().toISOString(),
      true, // Priority: user's self-updated avatar
    );
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Error updating profile picture");
  }
};

export const deleteUserAvatar = async (): Promise<void> => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    const userData = await getUserData(userFingerPrint.username);

    // delete from bucket
    if (userData.profile_image) {
      await deleteImage(userData.profile_image);
    }

    await patchUserData({
      ...userData,
      profile_image: "",
    });

    // remove the user's avatar from the map (priority: intentional self-delete)
    useUserAvatarMapState
      .getState()
      .removeUserAvatar(userFingerPrint.username, true);
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Error updating profile picture");
  }
};

export const commitPendingAuth = async (
  pending: PendingAuthData,
): Promise<void> => {
  // Fetch user data before committing auth state. The token is already in
  // Zustand from the loginUser TC path, so the request is authorized. If this
  // throws, no state has been committed and isLoggedIn stays false.
  const userData = await getUserData(pending.username);

  applyAuthBundle({
    username: pending.username,
    derivedKey: pending.derivedKey,
    token: pending.token,
    refreshToken: pending.refreshToken,
    privateKeys: pending.privateKeys,
    publicKeys: pending.publicKeys,
    encryptedPassword: pending.encryptedPassword,
    subscription: pending.subscription,
    kdfVersion: pending.kdfVersion,
    kdfSalt: pending.kdfSalt,
  });

  if (userData.profile_image) {
    useUserAvatarMapState
      .getState()
      .setUserAvatar(
        pending.username,
        userData.profile_image,
        new Date().toISOString(),
        true,
      );
  } else {
    useUserAvatarMapState.getState().removeUserAvatar(pending.username, true);
  }
};

export const deleteUserAccount = async (): Promise<void> => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    // Clear warm cache (IndexedDB epoch payloads) before account deletion
    await clearWarmCache();
    await deleteAccount();
    // logout user after account deletion
    logoutUser();
  } catch (error) {
    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Could not delete user account. Please contact support.");
  }
};
